// Angular
import { Injectable } from '@angular/core';

// Libraries
import { Select, Store } from '@ngxs/store';
import { BehaviorSubject, Observable, takeUntil } from 'rxjs';

// Models
import { NotificationModel } from '../models/notification-model';
import { HistoricalQueueDto, HistoricalQueueStatus } from '../../modules/job/models/historical-queue-dto.model';
import { AppCleanup, ToasterModel } from '../models';

// Store
import { StorageState } from '../state/storage.state';
import { StorageActions } from '../state/storage.action';
import { StatusType } from '../enums';
import { UtilityHelper } from '../helpers';

@Injectable({
	providedIn: 'root',
})
export class NotificationService extends AppCleanup {
	@Select(StorageState.toastHistory) toastHistory$: Observable<ToasterModel[]>;
	toasts: ToasterModel[];
	notifications: BehaviorSubject<NotificationModel[]> = new BehaviorSubject<NotificationModel[]>([]);

	constructor(private store: Store) {
		super();

		this.toastHistory$.pipe(takeUntil(this.destroy)).subscribe((toastHistoty) => {
			if (toastHistoty) {
				this.toasts = UtilityHelper.deepCopy(toastHistoty);
				let notifications = [];
				toastHistoty.forEach((toast) => {
					let notification: NotificationModel = {
						message: toast.message,
						messageDate: new Date(toast.date),
						status: toast.type,
					};
					notifications.push(notification);
				});

				this.notifications.next(notifications);
			}
		});
	}

	clearNotifications() {
		this.store.dispatch(new StorageActions.ClearToastHistory());
	}

	deleteNotification(index: number) {
		let toastIndex = this.toasts.length - index - 1; // Getting toast index, because once toast turns into notification, the order of toasts reverts
		this.toasts.splice(toastIndex, 1);
		this.store.dispatch(new StorageActions.SetToastHistory(this.toasts));
	}

	updateNotification(historicalQueue: HistoricalQueueDto) {
		let notifications = this.notifications.getValue();
		let currentNotification = notifications.find((n) => n.id === historicalQueue.id);

		if (currentNotification) {
			// Switch-case breaking, using if-else because I don't have time

			if (historicalQueue.historicalQueueStatusId == HistoricalQueueStatus.PendingMergeProcessing || historicalQueue.historicalQueueStatusId == HistoricalQueueStatus.PendingSplitProcessing) {
				currentNotification.status = StatusType.INFO;
				currentNotification.message =
					currentNotification.historicalQueueStatus === HistoricalQueueStatus.PendingMergeProcessing
						? `${currentNotification.fromJob.nameDisplay || currentNotification.fromJob.locationDisplay} is currently being merged to ${currentNotification.toJob.nameDisplay || currentNotification.toJob.locationDisplay}`
						: `${currentNotification.fromJob.nameDisplay || currentNotification.fromJob.locationDisplay} is currently being split to ${currentNotification.toJob.nameDisplay || currentNotification.toJob.locationDisplay}`;
			} else if (historicalQueue.historicalQueueStatusId == HistoricalQueueStatus.ProcessCompleteSplit || historicalQueue.historicalQueueStatusId == HistoricalQueueStatus.ProcessCompleteMerged) {
				currentNotification.status = StatusType.SUCCESS;
				currentNotification.message =
					currentNotification.historicalQueueStatus === HistoricalQueueStatus.PendingMergeProcessing
						? `Merge completed for ${currentNotification.fromJob.nameDisplay || currentNotification.fromJob.locationDisplay} to ${currentNotification.toJob.nameDisplay || currentNotification.toJob.locationDisplay}`
						: `Split completed for ${currentNotification.fromJob.nameDisplay || currentNotification.fromJob.locationDisplay} to ${currentNotification.toJob.nameDisplay || currentNotification.toJob.locationDisplay}`;
			} else if (historicalQueue.historicalQueueStatusId == HistoricalQueueStatus.ProcessFailed) {
				currentNotification.status = StatusType.DANGER;
				currentNotification.message =
					currentNotification.historicalQueueStatus === HistoricalQueueStatus.PendingMergeProcessing
						? `Merge failed for ${currentNotification.fromJob.nameDisplay || currentNotification.fromJob.locationDisplay} to ${currentNotification.toJob.nameDisplay || currentNotification.toJob.locationDisplay}`
						: `Split failed for ${currentNotification.fromJob.nameDisplay || currentNotification.fromJob.locationDisplay} to ${currentNotification.toJob.nameDisplay || currentNotification.toJob.locationDisplay}`;
			}

			currentNotification.messageDate = new Date();

			currentNotification.historicalQueueStatus = historicalQueue.historicalQueueStatusId;

			if (currentNotification.toJob?.id) currentNotification.route = `/job/${historicalQueue.toJobId}`;
		}
	}
}
